import { createFileRoute } from '@tanstack/react-router';
import { Seo, resolveSlugSeo } from '../-components/seo';
import { Button } from '@worldcoo/ui/components/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@worldcoo/ui/components/accordion';
import { CheckCircle2Icon, ChevronDownIcon, ImagesIcon, XCircleIcon } from 'lucide-react';
import { useLingui } from '@lingui/react/macro';
import { Layout, Paragraph, Title } from './-components/layout';
import { Header } from './-components/header';
import { Footer } from './-components/footer';
import { Image } from '../-components/image';
import { DetailsIcon, BreakdownIcon } from './-components/icons';
import { trpc } from '@/lib/trpc';
import { formatNumber } from '@worldcoo/ui/lib/utils';
import { useTranslation, type LandingLocale } from '@/lib/i18n';
import { slugs } from './transparency.meta';
import { wwwLocales } from './-components/utils';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute('/www/transparency')({
	loader: ({ context }) => {
		context.queryClient.ensureQueryData(
			trpc.portal.getWorldcooGlobalStats.queryOptions(undefined, { staleTime: 60 * 60 * 1000 }),
		);
	},
	component: Component,
});

function Component() {
	const { t } = useLingui();
	const { language } = useTranslation();
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());

	// Ensure language is one of the supported landing locales, fallback to 'es'
	const memoryLocale: LandingLocale = (['es', 'en', 'ca', 'it'] as const).includes(language as LandingLocale)
		? (language as LandingLocale)
		: 'es';

	const formattedProjectsFunded = formatNumber(language, stats.projectsFunded, 0);

	const formattedSocialOrgs = formatNumber(language, stats.socialOrganizations, 0);
	const { baseUrl, locale } = Route.useRouteContext();
	const { canonicalUrl, alternates } = resolveSlugSeo(baseUrl, locale, wwwLocales, slugs);

	return (
		<Layout>
			<Header />
			<Seo
				title={t`Transparency at Worldcoo`}
				description={t`Information about our transparency and processes`}
				canonicalUrl={canonicalUrl}
				alternates={alternates}
				locale={locale}
				alternateLocales={[...wwwLocales]}
			/>

			{/* Hero */}
			<section className="bg-hero flex flex-col gap-8">
				<div className="mx-auto flex max-w-7xl flex-col items-center gap-16 p-8 md:flex-row">
					<div className="flex w-full flex-col gap-8">
						<Title>{t`Transparency at Worldcoo`}</Title>
						<Paragraph>{t`Since 2012, we have transformed the way of shopping to build a better world, and since 2018, we have promoted Solidarity Rounding in stores. Thanks to this initiative, we have funded ${formattedProjectsFunded} projects with ${formattedSocialOrgs} global organizations. As a B Corp company, we continuously improve our processes to maximize social impact.`}</Paragraph>
					</div>
					<div className="hidden w-full justify-end md:flex">
						<Image
							src="/transparency.png"
							alt={t`A magnifying glass inspecting a document.`}
							height={398}
							aspectRatio={432 / 398}
							priority
							className="ml-auto"
						/>
					</div>
				</div>
			</section>

			{/* Annual Report */}
			<section className="bg-hero flex flex-col gap-8 p-8">
				<h2 className="text-primary text-center font-medium uppercase">{t`Our corporate reports`}</h2>
				<p className="mx-auto max-w-7xl text-center">{t`You can check the social impact achieved through Worldcoo (donations, funded projects, amounts, etc.) over the past years in our corporate reports.`}</p>
				<nav aria-label={t`Annual reports`}>
					<ul className="mx-auto grid w-full max-w-7xl grid-cols-3 gap-3 py-8 md:grid-cols-7">
						{memories.map(({ year, links }) => (
							<li key={year}>
								<Button size="2xl" asChild>
									<a
										key={year}
										href={links[memoryLocale] || links.es}
										target="_blank"
										rel="noreferrer noopener"
										aria-label={t`${year} annual report`}
										className="mx-auto w-full md:w-fit md:text-3xl"
									>
										{year}
									</a>
								</Button>
							</li>
						))}
					</ul>
				</nav>
			</section>

			{/* How Worldcoo Works */}
			<section className="mx-auto my-20 flex max-w-7xl flex-col gap-8 px-8">
				<h2 className="text-center text-2xl font-medium md:text-5xl">{t`How does Worldcoo work?`}</h2>
				<p className="mx-auto max-w-5xl text-center text-lg">{t`We are a technology company specializing in integrating micro-donation solutions (such as Solidarity Rounding) into the payment gateways of physical and online stores. Additionally, we manage the entire collaboration process between companies and beneficiary organizations, ensuring maximum efficiency and transparency.`}</p>
				<p className="text-primary text-center font-medium uppercase">{t`To establish a collaboration between an organization and a store:`}</p>

				<Accordion type="multiple" className="flex w-full flex-col gap-6" defaultValue={['organization-process']}>
					<AccordionItem value="organization-process" className="bg-secondary-hero rounded-md border-none px-4">
						<AccordionTrigger className="px-8 text-2xl font-medium">{t`Organization selection process:`}</AccordionTrigger>
						<AccordionContent className="flex flex-col gap-4 px-8">
							<p className="text-sm">{t`Organizations interested in joining our platform must contact us and undergo an evaluation process (screening). This analysis allows us to verify that they meet the established criteria to ensure they are eligible to receive funds.`}</p>

							<div className="flex gap-4 md:items-center">
								<CheckCircle2Icon className="text-primary m-2 size-9 shrink-0" strokeWidth={1} />
								<p>
									<span className="font-semibold">{t`If the organization meets the requirements:`}</span>{' '}
									{t`It is integrated into our database of organizations available to collaborate with stores. It is important to note that, as there are more organizations than stores, it is not always possible for all organizations to be selected. The final decision on which organization to collaborate with lies with the partner companies that integrate our fundraising channels.`}
								</p>
							</div>

							<div className="flex gap-4 md:items-center">
								<XCircleIcon className="text-primary m-2 size-9 shrink-0" strokeWidth={1} />
								<p>
									<span className="font-semibold">{t`If the organization does not meet the requirements:`}</span>{' '}
									{t`We provide detailed reasons for the rejection and offer the possibility to correct any necessary aspects to reapply for the evaluation process.`}
								</p>
							</div>
						</AccordionContent>
					</AccordionItem>

					<AccordionItem value="collaboration-planning" className="bg-secondary-hero rounded-md border-none px-4">
						<AccordionTrigger className="px-8 text-2xl font-medium">{t`Collaboration planning:`}</AccordionTrigger>
						<AccordionContent className="flex flex-col gap-4 px-8">
							<p className="text-sm">{t`Once the partner company decides to start a campaign with an organization, a project calendar is established. This calendar is planned annually, defining start and end dates. The duration of each project may vary depending on the agreements with each store.`}</p>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</section>

			{/* Fund Management */}
			<section className="mx-auto my-20 flex max-w-5xl flex-col items-center gap-16 px-8">
				<h2 className="text-center text-2xl font-medium md:text-5xl">{t`Management of raised funds`}</h2>
				<p className="mx-auto text-lg">{t`Once the collaboration between a store and an organization ends, we manage the process of transferring the raised funds. 100% of the total amount raised is transferred to the beneficiary social organization through two payments:`}</p>
				<div className="max-w-5xl">
					<Accordion
						type="multiple"
						className="flex w-full flex-col gap-8 md:flex-row md:gap-64"
						defaultValue={['first-payment']}
					>
						<AccordionItem value="first-payment" className="border-none md:w-1/2">
							<AccordionTrigger className="bg-secondary-hero rounded-md px-4 text-2xl font-medium">
								{t`First Payment`}
							</AccordionTrigger>
							<AccordionContent className="flex flex-col gap-4 py-3">
								<p className="text-base">{t`50% right at the end of the fundraising campaign.`}</p>
							</AccordionContent>
						</AccordionItem>

						<AccordionItem value="second-payment" className="border-none md:w-1/2">
							<AccordionTrigger className="bg-secondary-hero rounded-md px-4 text-2xl font-medium">
								{t`Second Payment`}
							</AccordionTrigger>
							<AccordionContent className="flex flex-col gap-4 py-3">
								<p className="text-base">{t`The remaining 50% when the organization sends us a detailed report justifying the use of the raised funds.`}</p>
							</AccordionContent>
						</AccordionItem>
					</Accordion>
				</div>
				<p className="text-primary text-center font-medium uppercase">{t`Worldcoo acts as a service provider for the NGO:`}</p>
				<p className="text-center text-3xl font-medium">{t`That is, once the funds are transferred, Worldcoo charges the organization for the services provided.`}</p>
			</section>

			{/* Agreements */}
			<section className="bg-hero my-24">
				<div className="mx-auto flex max-w-7xl flex-col items-center p-8 md:flex-row">
					<div className="flex w-full flex-col gap-8">
						<h2 className="text-center text-2xl font-semibold md:text-start md:text-5xl">{t`Commitments of beneficiary organizations`}</h2>
						<p className="text-base">{t`When an organization receives funds to finance its project, it must send a complete report on the project executed with the raised funds, including the following information:`}</p>
						<ul className="m-3 flex flex-col justify-between gap-8 text-center text-lg font-medium md:flex-row">
							<li className="flex flex-col items-center gap-6 md:w-1/5">
								<DetailsIcon className="text-primary h-12" />
								<p>{t`Details of the project development and the beneficiaries`}</p>
							</li>
							<li className="flex flex-col items-center gap-6 md:w-1/5">
								<ImagesIcon className="text-primary h-12" />
								<p>{t`Relevant images and documentation supporting the progress`}</p>
							</li>
							<li className="flex flex-col items-center gap-6 md:w-1/5">
								<BreakdownIcon className="text-primary h-12" />
								<p>{t`Detailed breakdown of how the received funds were spent`}</p>
							</li>
						</ul>
					</div>
					<div className="hidden w-full justify-center md:flex">
						<Image
							src="/beneficiaries.png"
							alt={t`A hand donating through a phone.`}
							aspectRatio={384 / 595}
							height={595}
						/>
					</div>
				</div>
			</section>

			{/* Tracking */}
			<section className="my-24">
				<div className="mx-auto flex max-w-5xl flex-col items-center gap-16 px-8">
					<h2 className="text-center text-2xl font-medium md:text-5xl">{t`Tracking and access to information`}</h2>
					<p className="text-center">{t`We provide all our users with an updated record of all campaigns carried out and the history of funded projects.`}</p>
					<p className="text-primary text-center font-medium uppercase">{t`You can check this information here:`}</p>
					<ul className="flex flex-col gap-8 uppercase">
						<li>
							<Button size="2xl" asChild>
								<a
									href="https://condis.worldcoo.com"
									target="_blank"
									rel="noreferrer noopener"
									aria-label={t`Condis donation portal`}
									className="w-full md:text-3xl"
								>
									condis.worldcoo.com
								</a>
							</Button>
						</li>
						<li>
							<Button size="2xl" asChild>
								<a
									href="https://bonpreu.worldcoo.com"
									target="_blank"
									rel="noreferrer noopener"
									aria-label={t`Bonpreu donation portal`}
									className="w-full md:text-3xl"
								>
									bonpreu.worldcoo.com
								</a>
							</Button>
						</li>
						<li>
							<Button size="2xl" asChild>
								<a
									href="https://ticketmaster.worldcoo.com"
									target="_blank"
									rel="noreferrer noopener"
									aria-label={t`Ticketmaster donation portal`}
									className="w-full md:text-3xl"
								>
									ticketmaster.worldcoo.com
								</a>
							</Button>
						</li>
					</ul>
					<p className="mt-16 text-center text-3xl font-medium">{t`At Worldcoo, we work every day to ensure that every cent donated reaches its destination with the greatest possible impact. Our transparency and commitment to society are the fundamental pillars that guide our work.`}</p>
				</div>
			</section>

			{/* Projects Info */}
			<section className="bg-hero py-24">
				<div className="mx-auto flex max-w-6xl flex-col items-center gap-16 px-8">
					<h2 className="text-center text-2xl font-medium md:text-5xl">{t`For more information about the projects:`}</h2>

					{/* For desktop */}
					<nav aria-label={t`Collaborating companies`} className="hidden w-full max-w-4xl md:block">
						<ul className="grid grid-cols-4 justify-between gap-4">
							{projects.map((project) => (
								<li key={project.url}>
									<a
										href={project.url}
										target="_blank"
										rel="noreferrer noopener"
										className="text-primary text-lg font-medium uppercase hover:underline"
									>
										{project.text}
									</a>
								</li>
							))}
						</ul>
					</nav>

					{/* For phones */}
					<div className="relative block md:hidden">
						<select
							aria-label={t`Collaborating companies`}
							onChange={(e) => {
								if (e.target.value) window.location.href = e.target.value;
							}}
							className="bg-background w-full appearance-none truncate rounded-md px-4 py-4 pr-16 pl-6 text-xl font-medium"
						>
							<option value="">{t`Collaborating companies`}</option>
							{projects.map((project) => (
								<option key={project.url} value={project.url}>
									{project.text}
								</option>
							))}
						</select>
						<ChevronDownIcon className="pointer-events-none absolute top-5 right-3 size-6" />
					</div>
				</div>
			</section>

			<Footer />
		</Layout>
	);
}

type Memory = {
	year: string;
	links: {
		es: string;
		en: string;
		ca: string;
		it: string;
	};
};

const memories: Memory[] = [
	{
		year: '2019',
		links: {
			es: 'https://drive.google.com/file/d/11GQHytfX-r3wfp0byrzFHUFdj1PMp--k/view?usp=drive_link',
			en: 'https://drive.google.com/file/d/1x3azVxsWLnP5C05dB09KNimFL3jKz55O/view?usp=drive_link',
			ca: 'https://drive.google.com/file/d/11Cpzb_lpjr0qd9tsWnb_B54gkQq4kF2G/view?usp=drive_link',
			it: 'https://drive.google.com/file/d/1x3azVxsWLnP5C05dB09KNimFL3jKz55O/view?usp=drive_link',
		},
	},
	{
		year: '2020',
		links: {
			es: 'https://drive.google.com/file/d/15q7vIbEtyj1989wQ5tFwfLk8IZKC62D-/view?usp=drive_link',
			en: 'https://drive.google.com/file/d/15kqDxfAWv11eTtg2DTK5-8ebjnhR6Pc4/view?usp=drive_link',
			ca: 'https://drive.google.com/file/d/15oEaqzlWivCO-NS34EEyYbBzArD8ftYV/view?usp=drive_link',
			it: 'https://drive.google.com/file/d/15kqDxfAWv11eTtg2DTK5-8ebjnhR6Pc4/view?usp=sharing',
		},
	},
	{
		year: '2021',
		links: {
			es: 'https://drive.google.com/file/d/11O4oTYrNBTdmNDekO7tvKlcgnw5hpeaz/view?usp=drive_link',
			en: 'https://drive.google.com/file/d/11M2V5DWFVpPZG9Ia0uULULCa4XQtCbLD/view?usp=drive_link',
			ca: 'https://drive.google.com/file/d/11PGrTzQB2fX81iBRTlA-kqgFG8OKtQiw/view?usp=drive_link',
			it: 'https://drive.google.com/file/d/11M2V5DWFVpPZG9Ia0uULULCa4XQtCbLD/view?usp=drive_link',
		},
	},
	{
		year: '2022',
		links: {
			es: 'https://drive.google.com/file/d/11VvkRdC1ITXQziBFBHNXIB_aBbAJdSUV/view?usp=drive_link',
			en: 'https://drive.google.com/file/d/11VvkRdC1ITXQziBFBHNXIB_aBbAJdSUV/view?usp=drive_link',
			ca: 'https://drive.google.com/file/d/11VvkRdC1ITXQziBFBHNXIB_aBbAJdSUV/view?usp=drive_link',
			it: 'https://drive.google.com/file/d/11VvkRdC1ITXQziBFBHNXIB_aBbAJdSUV/view?usp=drive_link',
		},
	},
	{
		year: '2023',
		links: {
			es: 'https://drive.google.com/file/d/1j1cQKVsPvsR1OzII8Mf3H_hrvYbJYG2B/view?usp=drive_link',
			en: 'https://drive.google.com/file/d/1j1cQKVsPvsR1OzII8Mf3H_hrvYbJYG2B/view?usp=drive_link',
			ca: 'https://drive.google.com/file/d/1qv4sgLYKJG4RD6yffdHeLkKXhg8rziTC/view?usp=drive_link',
			it: 'https://drive.google.com/file/d/1j1cQKVsPvsR1OzII8Mf3H_hrvYbJYG2B/view?usp=drive_link',
		},
	},
	{
		year: '2024',
		links: {
			es: 'https://drive.google.com/file/d/1r-kupm93JevZAhUESxsooHyFX_IdtwdP/view?usp=drive_link',
			en: 'https://drive.google.com/file/d/1umRWzio8RDvMPS_n-_fY1pgWA6Xk1pWU/view?usp=drive_link',
			ca: 'https://drive.google.com/file/d/1LlrnQgqv92pwOfgVCpac0NPkvielNab1/view?usp=drive_link',
			it: 'https://drive.google.com/file/d/1UzHzcW2E8sKQHKk-ivInpQb9OUq5ZAyN/view?usp=drive_link',
		},
	},
	{
		year: '2025',
		links: {
			es: 'https://drive.google.com/file/d/1FdmbDD-DzgmxwZbM1RaW98sBopiDGI0Y/view?usp=drive_link',
			en: 'https://drive.google.com/file/d/14OFpqkJDV09tkt0vyhw7Ul49CS9xfvQY/view?usp=drive_link',
			ca: 'https://drive.google.com/file/d/1NFaRjjpxaut8tRlccI93lhKBq3TnIaj4/view?usp=drive_link',
			it: 'https://drive.google.com/file/d/1vdbeR3xnRDZIeYSSYd3yKrp8pnJmLkIA/view?usp=drive_link',
		},
	},
];

const projects = [
	{ url: 'https://areas.worldcoo.com', text: 'areas' },
	{ url: 'https://awwg.worldcoo.com', text: 'awwg' },
	{ url: 'https://bonpreu.worldcoo.com', text: 'bonpreu i esclat' },
	{ url: 'https://caprabo.worldcoo.com', text: 'caprabo' },
	{ url: 'https://condis.worldcoo.com', text: 'condis' },
	{ url: 'https://correos.worldcoo.com', text: 'correos' },
	{ url: 'https://cottet.worldcoo.com', text: 'cottet' },
	{ url: 'https://doctipharma.worldcoo.com', text: 'doctipharma' },
	{ url: 'https://eroski.worldcoo.com', text: 'eroski' },
	{ url: 'https://familasicilia.worldcoo.com' },
	{ url: 'https://generaloptica.worldcoo.com', text: 'general optica' },
	{ url: 'https://glovo.worldcoo.com', text: 'glovo' },
	{ url: 'https://iberdrola.worldcoo.com', text: 'iberdrola' },
	{ url: 'https://ilcentesimo.worldcoo.com', text: 'ilcentesimo' },
	{ url: 'https://maxsupermercati.worldcoo.com', text: 'max supermercati' },
	{ url: 'https://mediamarkt.worldcoo.com', text: 'mediamarkt' },
	{ url: 'https://muymucho.worldcoo.com', text: 'muy mucho' },
	{ url: 'https://pccomponentes.worldcoo.com', text: 'pc componentes' },
	{ url: 'https://promofarma.worldcoo.com', text: 'promofarma' },
	{ url: 'https://repsol.worldcoo.com', text: 'repsol' },
	{ url: 'https://telefonica.worldcoo.com', text: 'telefonica' },
	{ url: 'https://ticketmaster.worldcoo.com', text: 'ticketmaster' },
	{ url: 'https://verdecora.worldcoo.com', text: 'verdecora' },
	{ url: 'https://viena.worldcoo.com', text: 'viena' },
	{ url: 'https://wala.worldcoo.com', text: 'wala' },
];
